"use client";

import Image from "next/image";
import Link from "next/link";
import { LiveBanner } from "./_components/dashboard/live-banner";
import { useMainnetDashboard } from "@/hooks/useMainnetDashboard";
import { formatPoints } from "@/components/mainnet/formatters";

const PARTNER_LOGOS = {
  reppo: "https://pulsarmoney.b-cdn.net/spicenet-portal/reppo-logo.png",
  zentra: "https://pulsarmoney.b-cdn.net/spicenet-portal/zentra-logo.png",
} as const;

const RECENT_BADGES = [
  {
    title: "Gold Pioneer",
    description: "Top-tier pre-mainnet contributor · 1.5x multiplier",
    icon: "/spicenet-dashboard/badge-gold-pioneer.svg",
  },
  {
    title: "Explorer",
    description: "Used 3 out of 5 Spice Flow apps",
    icon: "/spicenet-dashboard/badge-explorer.svg",
  },
  {
    title: "First Mover",
    description: "Used an app within its first week on mainnet",
    icon: "/spicenet-dashboard/badge-first-mover.svg",
  },
  {
    title: "Cross-Chain Pro",
    description: "100 cross-chain transactions",
    icon: "/spicenet-dashboard/badge-cross-chain-pro.svg",
  },
] as const;

function formatWholeNumber(value: number) {
  return Math.round(value).toLocaleString();
}

function formatUsdCompact(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: value >= 1000 ? "compact" : "standard",
    maximumFractionDigits: value >= 1000 ? 1 : 0,
  }).format(value);
}

function FrameCorners() {
  return (
    <>
      <span className="pointer-events-none absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 font-mono text-[13px] leading-none text-spicenet-black/32">+</span>
      <span className="pointer-events-none absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 font-mono text-[13px] leading-none text-spicenet-black/32">+</span>
      <span className="pointer-events-none absolute bottom-0 left-0 -translate-x-1/2 translate-y-1/2 font-mono text-[13px] leading-none text-spicenet-black/32">+</span>
      <span className="pointer-events-none absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 font-mono text-[13px] leading-none text-spicenet-black/32">+</span>
    </>
  );
}

function CornerDots() {
  return (
    <>
      <span className="pointer-events-none absolute left-2 top-2 h-[5px] w-[5px] bg-spicenet-black" />
      <span className="pointer-events-none absolute right-2 top-2 h-[5px] w-[5px] bg-spicenet-black" />
      <span className="pointer-events-none absolute bottom-2 left-2 h-[5px] w-[5px] bg-spicenet-black" />
      <span className="pointer-events-none absolute bottom-2 right-2 h-[5px] w-[5px] bg-spicenet-black" />
    </>
  );
}

function SectionFrame({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`relative border border-dashed border-spicenet-black/20 bg-white p-4 md:p-5 ${className}`}>
      <FrameCorners />
      {children}
    </section>
  );
}

function SummaryCard({
  label,
  value,
  sublabel,
  accent,
  emphasize = false,
  muted = false,
  strongMeta = false,
}: {
  label: string;
  value: string;
  sublabel: string;
  accent?: string;
  emphasize?: boolean;
  muted?: boolean;
  strongMeta?: boolean;
}) {
  return (
    <div className={emphasize ? "relative min-h-[144px] bg-[#F24E49] px-6 py-5 text-white" : "relative min-h-[144px] bg-white px-6 py-5"}>
      <CornerDots />
      <p className={`font-mono text-[9px] uppercase tracking-[0.22em] ${emphasize ? "text-white/72" : strongMeta ? "text-spicenet-black" : "text-spicenet-black/42"}`}>{label}</p>
      <p className={`mt-7 text-[50px] leading-none font-medium tracking-[-0.045em] ${emphasize ? "text-white" : muted ? "text-spicenet-black/38" : "text-spicenet-black"}`}>
        {value}
      </p>
      <div className="mt-4 flex items-center gap-2">
        {accent ? (
          <span
            className={`inline-flex items-center rounded-sm px-1.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] ${
              emphasize ? "bg-white font-semibold text-[#F24E49]" : "bg-transparent font-semibold text-[#F24E49]"
            }`}
          >
            {accent}
          </span>
        ) : null}
        <span className={`font-mono text-[10px] uppercase tracking-[0.16em] ${emphasize ? "text-white/76" : strongMeta ? "text-spicenet-black" : "text-spicenet-black/42"}`}>
          {sublabel}
        </span>
      </div>
    </div>
  );
}

function ActivityRow({
  name,
  volumeLabel,
  progressPercent,
  statLabel,
}: {
  name: string;
  volumeLabel: string;
  progressPercent: number;
  statLabel: string;
}) {
  return (
    <div className="grid grid-cols-[92px_1fr_auto] items-center gap-3">
      <div>
        <p className="text-sm font-medium text-spicenet-black">{name}</p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-spicenet-black/46">{volumeLabel}</p>
      </div>
      <div className="h-[8px] overflow-hidden rounded-full bg-[#ECE6DF]">
        <div
          className="h-full rounded-full bg-[#F24E49] transition-all duration-500 ease-out"
          style={{ width: `${Math.max(6, Math.min(progressPercent, 100))}%` }}
        />
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-spicenet-black/46">{statLabel}</p>
    </div>
  );
}

function CampaignListItem({
  partnerId,
  name,
  description,
  scoreLabel,
}: {
  partnerId: "reppo" | "zentra";
  name: string;
  description: string;
  scoreLabel: string;
}) {
  return (
    <div className="flex items-center gap-3 border-b border-spicenet-black/8 py-3 last:border-b-0">
      <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-spicenet-black/8 bg-[#F8F6F2]">
        <Image src={PARTNER_LOGOS[partnerId]} alt={name} width={28} height={28} className="h-7 w-auto object-contain" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-spicenet-black">{name}</p>
        <p className="truncate font-mono text-[10px] uppercase tracking-[0.14em] text-spicenet-black/46">{description}</p>
      </div>
      <div className="text-right">
        <span className="inline-flex items-center rounded-sm bg-[#FFF1EE] px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#F24E49]">
          {scoreLabel}
        </span>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { mainnetScore, legacyScore, activeBoosts, campaigns, leaderboardPosition, isLoading, isError } = useMainnetDashboard();

  return (
    <div className="mx-auto w-full max-w-[1160px] px-3 pb-16 md:px-5">
      <LiveBanner message="Spice Flow mainnet campaigns are now centered on partner milestones and tier-based claims." />

      <SectionFrame className="mt-6">
        <div className="grid grid-cols-1 border border-spicenet-black/8 divide-y divide-spicenet-black/8 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
          <SummaryCard
            label="Mainnet Score"
            value={formatWholeNumber(mainnetScore)}
            sublabel="Active multiplier"
            accent={activeBoosts.length > 0 ? `${activeBoosts.length} live boost${activeBoosts.length > 1 ? "s" : ""}` : undefined}
            emphasize
          />
          <SummaryCard
            label="Legacy Score"
            value={formatWholeNumber(legacyScore)}
            sublabel="Pre-mainnet · original portal campaign"
            muted
            strongMeta
          />
          <SummaryCard
            label="Leaderboard Position"
            value={leaderboardPosition ? `#${leaderboardPosition}` : "—"}
            sublabel="Across mainnet campaigns"
          />
        </div>
      </SectionFrame>

      <SectionFrame className="mt-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.08fr_0.92fr]">
          <div className="border border-spicenet-black/8 bg-white p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-spicenet-black/46">Your Activity</p>
            <div className="mt-4 space-y-4">
              {isLoading ? (
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-spicenet-black/46">
                  Loading mainnet activity...
                </p>
              ) : null}
              {isError ? (
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#EA4B4B]">
                  Mainnet activity is temporarily unavailable.
                </p>
              ) : null}
              {campaigns.map((campaign) => {
                const claimedCount = campaign.tiers.filter((tier) => tier.state === "claimed").length;
                const progressPercent = campaign.nextTierProgressPercent ?? 100;

                return (
                  <ActivityRow
                    key={campaign.partnerId}
                    name={campaign.name}
                    volumeLabel={formatUsdCompact(campaign.totalUserVolumeUsd)}
                    progressPercent={progressPercent}
                    statLabel={`${claimedCount} / ${campaign.tiers.length} tiers claimed`}
                  />
                );
              })}
            </div>
          </div>

          <div className="border border-spicenet-black/8 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-spicenet-black/46">Active Campaigns</p>
              <Link
                href="/campaigns"
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-spicenet-primary transition-colors hover:text-spicenet-primary/80"
              >
                View all
              </Link>
            </div>

            <div className="mt-4">
              {isLoading ? (
                <p className="pb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-spicenet-black/46">
                  Loading active campaigns...
                </p>
              ) : null}
              {isError ? (
                <p className="pb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-[#EA4B4B]">
                  Campaign data is temporarily unavailable.
                </p>
              ) : null}
              {campaigns.map((campaign) => {
                const scoreLabel = formatPoints(campaign.claimedPointsTotal).replace(" pts", " pts");

                return (
                  <CampaignListItem
                    key={campaign.partnerId}
                    partnerId={campaign.partnerId}
                    name={campaign.name}
                    description={`${campaign.chainName} · ${campaign.poolDisplayText}`}
                    scoreLabel={scoreLabel}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </SectionFrame>

      <section className="mt-6 border border-spicenet-black/8 bg-[#FDFDFC] px-5 py-5">
        <div className="flex items-center justify-between gap-3">
          <p className="font-mono text-xs font-semibold uppercase tracking-[-0.03em] text-[#3F3D3C]">Recent Badges</p>
          <Link
            href="/profile"
            className="inline-flex items-center gap-2 font-mono text-sm font-semibold uppercase tracking-[-0.03em] text-[#EA4B4B] transition-colors hover:text-[#EA4B4B]/80"
          >
            View All
            <span aria-hidden="true" className="text-base leading-none">→</span>
          </Link>
        </div>

        <div className="mt-[17px] grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {RECENT_BADGES.map((badge) => (
            <div key={badge.title} className="flex min-h-[156px] flex-col items-center justify-center gap-[10px] bg-[#FBEBEA] px-5 py-5 text-center">
              <Image src={badge.icon} alt={badge.title} width={48} height={48} className="h-12 w-12 object-contain" />
              <div className="flex flex-col items-center gap-2">
                <p className="text-[18px] font-medium leading-[22px] tracking-[-0.027em] text-[#262523]">{badge.title}</p>
                <p className="max-w-[210px] font-mono text-xs uppercase leading-[14px] tracking-[-0.03em] text-[#3F3D3C]">
                  {badge.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
